import type { ReactNode } from "react";

export type PortalPrintDocumentKind = "visit-packet" | "daily-list";

interface PortalPrintMetadataItem {
  readonly label: string;
  readonly value: ReactNode;
}

interface PortalPrintDocumentProps {
  readonly children: ReactNode;
  readonly documentId: string;
  readonly kind: PortalPrintDocumentKind;
  readonly title: string;
  readonly subtitle?: string;
  readonly metadata?: readonly PortalPrintMetadataItem[];
  readonly footer?: ReactNode;
}

const SAFE_DOCUMENT_ID = /^[a-z][a-z0-9-]{0,63}$/;

export function PortalPrintDocument({
  children,
  documentId,
  kind,
  title,
  subtitle,
  metadata = [],
  footer,
}: PortalPrintDocumentProps) {
  if (!SAFE_DOCUMENT_ID.test(documentId)) {
    throw new Error("Print document IDs must be static, safe names.");
  }

  const titleId = `${documentId}-print-title`;

  return (
    <article
      className={`portal-print-document portal-print-document--${kind}`}
      data-print-document=""
      data-print-kind={kind}
      aria-labelledby={titleId}
    >
      <header className="portal-print-document__header">
        <div>
          <p className="portal-print-document__brand">
            Whitehaven Wellness Hub
          </p>
          <h1 id={titleId}>{title}</h1>
          {subtitle && (
            <p className="portal-print-document__subtitle">{subtitle}</p>
          )}
        </div>
        {metadata.length > 0 && (
          <dl className="portal-print-document__metadata">
            {metadata.map((item) => (
              <div key={item.label}>
                <dt>{item.label}</dt>
                <dd>{item.value}</dd>
              </div>
            ))}
          </dl>
        )}
      </header>
      <div className="portal-print-document__body">{children}</div>
      {footer && (
        <footer className="portal-print-document__footer">{footer}</footer>
      )}
    </article>
  );
}
